/**
 * Non-`acatv2` courses admin-preview credentials, mirroring membership-hmi-preview
 * `http-common.ts#attachHttpRequestAuth` so native host themes (Aura) authenticate exactly like the
 * federated remote does:
 *
 * - **Builder IAM** — a raw CRM agency IAM JWT (no `v2.` prefix) sent straight to membership-backend
 *   as `source: WEB_USER`. The backend's IAM `verifyByUser` validates agency → location access on
 *   every request, so the preview location header is verified server-side, not trusted.
 * - **Legacy `acat`** — the Firebase admin session the remote mints via `/user/login-token` +
 *   `signInWithCustomToken`. Read-only here; the host never mints it.
 *
 * Both are gated on THIS tab's preview record (`cp-admin-preview`, per-tab sessionStorage — see
 * `admin-preview-token.utils.ts`), never on the browser-wide `capm` cookie or `acat` alone, so a
 * leftover agency token or `acat` never turns another tab / a plain learner visit into an admin
 * request, and the preview location always comes from the tab that entered it. Learner `cat` /
 * `catv2` are never read or written here.
 *
 * Client-only: every read returns null on SSR.
 */
import Cookies from 'js-cookie'

import {
  readActiveAdminPreviewTabRecord,
  readAdminPreviewAgencyToken,
  type AdminPreviewTabMode,
  type AdminPreviewTabRecord,
} from './admin-preview-token.utils'

/** Prefix of client-portal magic-link tokens that must be exchanged (never sent as a Bearer). */
export const ADMIN_V2_MAGIC_LINK_TOKEN_PREFIX = 'v2.' as const

/** Storage key the federated remote uses for its legacy Firebase admin session. */
export const LEGACY_ADMIN_TOKEN_KEY = 'acat' as const

const ADMIN_ROLE = 'admin' as const

export interface AdminPreviewCredential {
  token: string
  locationId: string
  /** Credential expiry (ms epoch) when known: JWT `exp` for builder IAM, `tokenExpirationTime` for `acat`. */
  expiresAt?: number | null
}

/** True for a client-portal `v2.` magic-link token (exchanged at `/user/v2/login-token`). */
export function isAdminV2MagicLinkToken(token: string | null | undefined): boolean {
  return typeof token === 'string' && token.startsWith(ADMIN_V2_MAGIC_LINK_TOKEN_PREFIX)
}

/** This tab's active preview record when it was entered with `mode`, else null. */
function readActiveTabRecordForMode(mode: AdminPreviewTabMode): AdminPreviewTabRecord | null {
  const record = readActiveAdminPreviewTabRecord()
  return record?.mode === mode ? record : null
}

/** Decode a base64url JWT segment. Returns null when it is not valid base64-JSON. */
function decodeJwtSegment(segment: string): Record<string, unknown> | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const parsed = JSON.parse(atob(padded)) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/**
 * Expiry (ms epoch) of a JWT from its `exp` claim, or null when the token is not a decodable JWT.
 * Not a signature check — only used to time-box the client-side preview record.
 */
export function readJwtExpiryMs(token: string | null | undefined): number | null {
  const payloadSegment = typeof token === 'string' ? token.split('.')[1] : undefined
  if (!payloadSegment) return null
  const exp = decodeJwtSegment(payloadSegment)?.exp
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null
}

/**
 * Builder IAM credential: the per-tab agency JWT plus the location this tab entered the preview
 * with. Requires an active `builderIam` tab record (defaults to the stored one). `v2.` magic-link
 * tokens are excluded — they are only ever exchanged, never sent as a Bearer.
 */
export function readBuilderIamPreviewCredential(
  record: AdminPreviewTabRecord | null = readActiveTabRecordForMode('builderIam')
): AdminPreviewCredential | null {
  if (record?.mode !== 'builderIam' || record.exp <= Date.now()) return null
  const token = readAdminPreviewAgencyToken()
  if (!token || isAdminV2MagicLinkToken(token)) return null
  return { token, locationId: record.locationId, expiresAt: readJwtExpiryMs(token) }
}

interface LegacyAdminSession {
  tokenId?: string
  tokenExpirationTime?: number | string
  locationId?: string
  role?: string
}

function decodeLegacyAdminSession(raw: string | null | undefined): LegacyAdminSession | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(atob(raw)) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as LegacyAdminSession) : null
  } catch {
    // Not a base64-JSON `acat` session — treated as absent.
    return null
  }
}

function readLegacyAdminRaw(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return Cookies.get(LEGACY_ADMIN_TOKEN_KEY) || window.localStorage?.getItem(LEGACY_ADMIN_TOKEN_KEY) || null
  } catch {
    // Storage blocked (privacy mode / sandboxed iframe) — no legacy session available.
    return null
  }
}

export interface LegacyAdminCredentialOptions {
  /** Raw `acat` value; defaults to the cookie, then localStorage. */
  raw?: string | null
  /** Preview location to match; defaults to this tab's active `acat` preview record location. */
  locationId?: string | null
}

/**
 * Legacy Firebase admin credential (`acat`): requires an unexpired Firebase ID token, an admin role
 * when the session carries one, and a location matching the preview location.
 */
export function readLegacyAdminPreviewCredential(options: LegacyAdminCredentialOptions = {}): AdminPreviewCredential | null {
  const session = decodeLegacyAdminSession(options.raw === undefined ? readLegacyAdminRaw() : options.raw)
  if (!session?.tokenId) return null
  if (session.role && session.role !== ADMIN_ROLE) return null
  const expiresAt = parseInt(String(session.tokenExpirationTime ?? ''), 10)
  if (Number.isNaN(expiresAt) || expiresAt <= Date.now()) return null
  const locationId = options.locationId === undefined ? (readActiveTabRecordForMode('acat')?.locationId ?? null) : options.locationId
  if (!locationId || (session.locationId && session.locationId !== locationId)) return null
  return { token: session.tokenId, locationId, expiresAt }
}

export interface AdminPreviewAuthFailure {
  mode: string
  /** HTTP status of the rejected admin request; `0` when no admin credential was available to send. */
  status: number
  /** `credentialUnavailable`: this tab is in a preview but its admin credential expired / was cleared. */
  reason?: 'rejected' | 'credentialUnavailable'
}

type AdminPreviewAuthFailureListener = (failure: AdminPreviewAuthFailure) => void

const authFailureListeners = new Set<AdminPreviewAuthFailureListener>()

/**
 * Subscribe to admin-preview auth failures (a rejected admin-authenticated request, or a courses call
 * from a preview tab whose admin credential is gone). Lets the
 * courses layer flip its session status without the common HTTP layer importing it.
 * Returns an unsubscribe function.
 */
export function onAdminPreviewAuthFailure(listener: AdminPreviewAuthFailureListener): () => void {
  authFailureListeners.add(listener)
  return () => authFailureListeners.delete(listener)
}

export function emitAdminPreviewAuthFailure(failure: AdminPreviewAuthFailure): void {
  authFailureListeners.forEach(listener => listener(failure))
}
